import _ from 'lodash'
import { CoTrustedPartner, GoldenTicketType, ITimeLineConfig, RentalInfoPeriod } from 'Modules'
import { formatUnits, parseUnits } from 'viem'

import { getTokenDecimals, PHASE_ITEMS, TIER_ITEMS } from 'constants/pto'

/**
 * Clean metadata object - remove empty values
 */
export const cleanMetadata = (metadata: any) => {
  if (_.isNil(metadata)) return null

  if (!_.isPlainObject(metadata)) return null

  const cleaned = _.pickBy(metadata, (v) => {
    if (_.isArray(v)) return v.length > 0
    if (_.isObject(v)) return !_.isEmpty(v)
    return !_.isNil(v) && v !== ''
  })

  return _.isEmpty(cleaned) ? null : cleaned
}

/**
 * Clean golden tickets - filter out empty items
 * @param goldenTickets - array of specials from form
 */
export const cleanGoldenTickets = (goldenTickets: any[]) => {
  if (!goldenTickets || goldenTickets.length === 0) return []

  return goldenTickets
    .filter((item) => {
      const hasName = item.name && item.name.trim() !== ''
      const hasDescription = item.description && item.description.trim() !== ''
      const hasQuantity = item.quantity && item.quantity > 0
      const hasImage = item.image && item.image.trim() !== ''
      return hasName || hasDescription || hasQuantity || hasImage
    })
    ?.map(({ isNewRecord: _, rarityId: __, ...item }) => ({
      ...item,
    }))
}

/**
 * Clean co-trusted partners - trim values and filter out empty items
 * @param partners - array of partners from form
 */
export const cleanCoTrustedPartners = (partners: any[]) => {
  if (!partners || partners.length === 0) return []

  return partners
    .map((item) => ({
      name: (item?.name || '').trim(),
      logo: (item?.logo || '').trim(),
      website: (item?.website || '').trim(),
    }))
    .filter((item) => item.name || item.logo || item.website)
}

/**
 * Clean phases - filter enabled phases and format for API
 */
export const cleanPhases = (phases: ITimeLineConfig[]) => {
  const enabledPhases = phases.filter((phase) => phase.enabled)

  return enabledPhases.map((phase) => {
    const phaseAny = phase as any
    let maxNFTsWhitelisted = phaseAny.maxNFTsWhitelisted || 0
    const cleanedPhase: any = {
      tierLimits: [],
      name: phase.name,
      startTime: phase.startTime,
      endTime: phase.endTime,
      isEndTimeUnlimited: phase.isEndTimeUnlimited,
      isPurchasePhase: phase.isPurchasePhase,
      isClaimPhase: phaseAny.isClaimPhase || false,
      maxNFTsWhitelisted: maxNFTsWhitelisted || 0,
      nftsReserved: phaseAny.nftsReserved || 0,
      maxNFTs: phase.isPurchasePhase ? phaseAny.maxNFTs || 0 : 0,
    }

    if (phase.isPurchasePhase && phase.tierLimits && phase.tierLimits.length > 0) {
      cleanedPhase.tierLimits = phase.tierLimits
        .filter((tier: any) => tier.allowNftCollection)
        .map((tier: any) => ({
          tierId: tier.tierId,
          maxNFTs: tier.maxNFTs || 0,
          nftsReserved: tier.nftsReserved || 0,
        }))
      maxNFTsWhitelisted = phase.tierLimits.find((c: any) => !c.allowNftCollection)?.maxNFTs || 0
    }
    cleanedPhase.maxNFTsWhitelisted = maxNFTsWhitelisted

    return cleanedPhase
  })
}

/**
 * Initialize timelines for create mode
 */
export const initTimeLinesCreate = () => {
  return PHASE_ITEMS.map(
    (item) =>
      ({
        name: item.name,
        required: item.required || false,
        enabled: item.enabled || false,
        startTime: 0,
        endTime: 0,
        isEndTimeUnlimited: item.isEndTimeUnlimited,
        isPurchasePhase: item.isPurchasePhase,
        maxNFTs: 0,
        maxNFTsWhitelisted: 0,
        nftsReserved: 0,
        isClaimPhase: item.isClaimPhase || false,
        enabledTierLimit: false,
        tierLimits: item.isPurchasePhase
          ? TIER_ITEMS.map((tier) => {
              return {
                tierId: tier.tierId,
                name: tier.name,
                maxNFTs: 0,
                nftsReserved: 0,
                allowNftCollection: tier.allowNftCollection,
              }
            })
          : [],
      }) as ITimeLineConfig
  )
}

/**
 * Initialize timelines for edit mode with existing phases data
 */
export const initTimeLinesEdit = (phases?: ITimeLineConfig[]) => {
  return PHASE_ITEMS.map((item) => {
    const phase = phases?.find((phase) => phase.name === item.name)

    const enabledTierLimit = phase?.isPurchasePhase
      ? !!phase?.tierLimits?.find((tierLimit) => Number(tierLimit?.maxNFTs) > 0) || false
      : false

    return {
      name: phase?.name || item.name,
      required: phase?.required || item.required || false,
      enabled: !!phase,
      startTime: phase?.startTime || 0,
      endTime: phase?.endTime || 0,
      isEndTimeUnlimited: phase?.isEndTimeUnlimited || item.isEndTimeUnlimited,
      isPurchasePhase: phase?.isPurchasePhase || item.isPurchasePhase,
      maxNFTs: phase?.maxNFTs || 0,
      maxNFTsWhitelisted: phase?.maxNFTsWhitelisted || 0,
      nftsReserved: phase?.nftsReserved || 0,
      isClaimPhase: phase?.isClaimPhase || false,
      enabledTierLimit: enabledTierLimit,
      tierLimits: phase?.isPurchasePhase
        ? TIER_ITEMS.map((tier) => {
            const tierLimit = phase?.tierLimits?.find(
              (tierLimit) => tierLimit.tierId === tier.tierId
            )
            return {
              tierId: tier.tierId,
              name: tier.name,
              maxNFTs: tier.allowNftCollection
                ? tierLimit?.maxNFTs || 0
                : phase?.maxNFTsWhitelisted || 0,
              nftsReserved: tierLimit?.nftsReserved || 0,
            }
          })
        : [],
    } as ITimeLineConfig
  })
}

/**
 * Initialize tokenize specials
 */
export const INITIAL_GOLDEN_TICKET: GoldenTicketType = {
  name: '',
  description: '',
  quantity: null as unknown as number,
  image: '',
  rarityId: undefined,
  isNewRecord: true,
}

export const INITIAL_CO_TRUSTED_PARTNER: CoTrustedPartner = {
  name: '',
  logo: '',
  website: '',
}

/**
 * Normalize terms from API for form field (string).
 * Supports legacy plain string and current { content, flowType } shape.
 */
export const normalizeTermsContent = (terms: unknown): string => {
  if (terms == null) return ''
  if (typeof terms === 'string') return terms
  if (typeof terms === 'object' && terms !== null && 'content' in terms) {
    const content = (terms as { content?: unknown }).content
    return typeof content === 'string' ? content : ''
  }
  return ''
}

/**
 * rewardsPerNFT is stored/sent in token base units (USDC x10^6, MEY x10^18),
 * while the form shows it in human units - convert on the way in and out.
 */
export const formatRewardsPerNFT = (
  value: number | string | null | undefined,
  tokenSymbol?: string
): number | null => {
  if (value === null || value === undefined || value === '') return null

  const decimals = getTokenDecimals(tokenSymbol || '')
  const raw = String(value)

  // integer base units keep full precision through BigInt
  if (/^\d+$/.test(raw)) return Number(formatUnits(BigInt(raw), decimals))

  const num = Number(raw)
  return Number.isFinite(num) ? num / 10 ** decimals : null
}

/** Form value (human units) -> base units string, so 18-decimal values keep precision */
export const parseRewardsPerNFT = (
  value: number | string | null | undefined,
  tokenSymbol?: string
): string => parseUnits(String(value ?? 0), getTokenDecimals(tokenSymbol || '')).toString()

/** PTO detail API -> form values */
export const mapRentalPeriodsFromApi = (periods?: RentalInfoPeriod[]): RentalInfoPeriod[] =>
  (periods || []).map((period) => ({
    ...period,
    rewardsPerNFT: formatRewardsPerNFT(period.rewardsPerNFT, period.tokenSymbol) as number,
  }))

/** Form values -> create/update rental info payload */
export const toRentalPeriodPayload = (period: RentalInfoPeriod) =>
  ({
    ...period,
    rewardsPerNFT: parseRewardsPerNFT(period.rewardsPerNFT, period.tokenSymbol),
    contractAddress: period.contractAddress || '',
  }) as unknown as RentalInfoPeriod
